/**
 * My Assignments Page
 *
 * Team member view for viewing and completing training assignments.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import {
  useListMyAssignmentsQuery,
  useStartAssignmentSessionMutation,
  type TrainingAssignment,
  type TrainingAssignmentStatus,
} from '../store/api/trainingApi';
import { VersionLabel } from '../components/training/trainingUi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

type TabFilter = 'all' | 'active' | 'completed';

export function MyAssignmentsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabFilter>('all');

  const { data: assignments, isLoading, isError, error, refetch } = useListMyAssignmentsQuery();
  const { data: serverCaps } = useGetCapabilitiesQuery();
  const [startSession] = useStartAssignmentSessionMutation();
  /** The card being started — only that card shows "Starting...". */
  const [startingId, setStartingId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  // Filter assignments based on tab
  const filteredAssignments = assignments?.filter((a) => {
    if (tab === 'active') return a.status === 'assigned' || a.status === 'in_progress';
    if (tab === 'completed') return a.status === 'completed' || a.status === 'failed';
    return true;
  });

  const handleStartTraining = async (assignmentId: string) => {
    setStartingId(assignmentId);
    setStartError(null);
    try {
      const config = await startSession(assignmentId).unwrap();
      // PracticePage reads this config: it links the practice session to the
      // assignment (trainingAssignmentId) and shows the result on finalize.
      navigate('/practice', {
        state: { trainingConfig: config },
      });
    } catch (error) {
      console.error('Failed to start training session:', error);
      setStartError('This training could not be started right now. Please try again.');
    } finally {
      setStartingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // A failed load is not "you have no training assignments yet".
  if (isError) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">My Training Assignments</h1>
        <LoadErrorNotice
          error={error}
          onRetry={() => void refetch()}
          failedMessage="Your training assignments could not be loaded."
          testIdPrefix="my-assignments"
        />
      </div>
    );
  }

  // Group assignments by organization
  const groupedByOrg = filteredAssignments?.reduce<Record<string, TrainingAssignment[]>>(
    (acc, assignment) => {
      const orgName = assignment.organization?.name || 'Unknown Organization';
      if (!acc[orgName]) acc[orgName] = [];
      acc[orgName].push(assignment);
      return acc;
    },
    {}
  );

  const tabCounts = {
    all: assignments?.length || 0,
    active: assignments?.filter((a) => a.status === 'assigned' || a.status === 'in_progress').length || 0,
    completed: assignments?.filter((a) => a.status === 'completed' || a.status === 'failed').length || 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Training Assignments</h1>
        <p className="mt-1 text-gray-500">
          Complete your assigned training modules to develop your skills
        </p>
        {serverCaps?.teamChallenges === true && (
          <Link to="/challenges" className="mt-2 inline-block text-sm text-primary-600 hover:underline">
            Team challenges →
          </Link>
        )}
      </div>

      {startError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
          {startError}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {(['all', 'active', 'completed'] as const).map((tabKey) => (
            <button
              key={tabKey}
              onClick={() => setTab(tabKey)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                tab === tabKey
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tabKey.charAt(0).toUpperCase() + tabKey.slice(1)}
              <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                {tabCounts[tabKey]}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Assignments */}
      {!filteredAssignments?.length ? (
        <div className="card p-12 text-center">
          <ClipboardIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            {tab === 'all'
              ? 'No Training Assignments'
              : tab === 'active'
              ? 'No Active Assignments'
              : 'No Completed Assignments'}
          </h2>
          <p className="text-gray-500">
            {tab === 'all'
              ? 'You have no training assignments yet. Check back later!'
              : tab === 'active'
              ? 'You have no active training. Great job staying on top of things!'
              : 'Complete some assignments to see them here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedByOrg || {}).map(([orgName, orgAssignments]) => (
            <div key={orgName}>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BuildingIcon className="w-5 h-5 text-gray-400" />
                {orgName}
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {orgAssignments.map((assignment) => (
                  <AssignmentCard
                    key={assignment.id}
                    assignment={assignment}
                    onStart={() => handleStartTraining(assignment.id)}
                    isStarting={startingId === assignment.id}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface AssignmentCardProps {
  assignment: TrainingAssignment;
  onStart: () => void;
  isStarting: boolean;
}

function AssignmentCard({ assignment, onStart, isStarting }: AssignmentCardProps) {
  const statusConfig: Record<TrainingAssignmentStatus, { color: string; label: string }> = {
    assigned: { color: 'bg-blue-100 text-blue-800', label: 'Not Started' },
    in_progress: { color: 'bg-yellow-100 text-yellow-800', label: 'In Progress' },
    completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
    failed: { color: 'bg-red-100 text-red-800', label: 'Needs Retry' },
  };

  const status = statusConfig[assignment.status];
  const isActive = assignment.status === 'assigned' || assignment.status === 'in_progress';
  const canRetry = assignment.status === 'failed';
  const isOverdue = assignment.dueDate && new Date(assignment.dueDate) < new Date() && isActive;

  return (
    <div className="card overflow-hidden">
      <div className="p-5">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-3">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
            {status.label}
          </span>
          {isOverdue && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
              Overdue
            </span>
          )}
        </div>

        {/* Module Info */}
        <h3 className="font-semibold text-gray-900 mb-1">{assignment.module?.name}</h3>
        {/* The exact version this assignment was made at (a later edit of the module never changes it). */}
        {assignment.moduleVersion && (
          <p className="mb-1">
            <VersionLabel version={assignment.moduleVersion.version} contentHash={assignment.moduleVersion.contentHash} />
          </p>
        )}
        {assignment.module?.description && (
          <p className="text-sm text-gray-500 mb-4 line-clamp-2">{assignment.module.description}</p>
        )}

        {/* Score Display */}
        {typeof assignment.lastScoreOverall === 'number' && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-500">Last Score</span>
              <span className="font-medium text-gray-900">{assignment.lastScoreOverall}%</span>
            </div>
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  assignment.lastScoreOverall >= (assignment.module?.minOverallScore || 70)
                    ? 'bg-green-500'
                    : 'bg-yellow-500'
                }`}
                style={{ width: `${assignment.lastScoreOverall}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Passing score: {assignment.module?.minOverallScore || 70}%
            </p>
          </div>
        )}

        {/* Due Date */}
        {assignment.dueDate && (
          <p className={`text-sm mb-4 ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
            <CalendarIcon className="w-4 h-4 inline mr-1" />
            Due: {new Date(assignment.dueDate).toLocaleDateString()}
          </p>
        )}

        {/* Action Button */}
        {(isActive || canRetry) && (
          <button
            onClick={onStart}
            disabled={isStarting}
            className="w-full btn btn-primary"
          >
            {isStarting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Starting...
              </span>
            ) : canRetry ? (
              'Retry Training'
            ) : assignment.status === 'in_progress' ? (
              'Continue Training'
            ) : (
              'Start Training'
            )}
          </button>
        )}

        {/* Completed State */}
        {assignment.status === 'completed' && (
          <div className="flex items-center justify-center gap-2 text-green-600 py-2">
            <CheckIcon className="w-5 h-5" />
            <span className="font-medium">Completed</span>
            {assignment.completedAt && (
              <span className="text-gray-400 text-sm">
                on {new Date(assignment.completedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Icons
function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
